import React from 'react';
import useAutocomplete from '@mui/material/useAutocomplete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import { autocompleteClasses } from '@mui/material/Autocomplete';

const Root = styled('div')(({ theme }) => ({
  fontSize: '14px',
   position: 'relative', // ✅ critical for positioning dropdown
}));

const Label = styled('label')`
  padding: 0 0 4px;
  display: block;
`;

const InputWrapper = styled('div')(({ theme }) => ({
  border: '1px solid #d9d9d9',
 backgroundColor: 'transparent',
  borderRadius: '4px',
  padding: '1px',
  display: 'flex',
    minHeight: '40px', // ✅ Match MUI TextField height
    alignItems: 'center', // Center content vertically
  flexWrap: 'wrap',
  '&.focused': {
  borderColor: '#d9d9d9',
  boxShadow: 'none',
},
  '& input': {
    //backgroundColor: '#fff',
     backgroundColor: 'transparent', // ✅ Add this
    height: '30px',
    padding: '4px 6px',
    minWidth: '30px',
    flexGrow: 1,
    border: 0,
    outline: 0,
  },
}));

const Listbox = styled('ul')(() => ({
  margin: 2,
  padding: 0,
  position: 'absolute',
  listStyle: 'none',
  maxHeight: 250,
  overflow: 'auto',
  borderRadius: 4,
  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  zIndex: 1,
width: '100%', // ✅ fixes oversize issue                // ✅ Prevents expanding to page width
 backgroundColor: 'transparent',
  '& li': {
    padding: '5px 12px',
    display: 'flex',
    '& span': { flexGrow: 1 },
    '& svg': { color: 'transparent' },
  },
  "& li[aria-selected='true']": {
    backgroundColor: 'transparent',
    fontWeight: 600,
    '& svg': { color: '#1890ff' },
  },
  [`& li.${autocompleteClasses.focused}`]: {
    backgroundColor: 'transparent',
    cursor: 'pointer',
    '& svg': { color: 'currentColor' },
  },
}));

interface OptionType {
  label: string;
  value: string | number;
}

interface TagProps {
  label: string;
  onDelete?: (...args: any[]) => void;
}

const StyledTag = styled(({ label, onDelete, ...other }: TagProps) => (
  <div {...other}>
    <span>{label}</span>
    <CloseIcon onClick={onDelete} />
  </div>
))(() => ({
  display: 'flex',
  alignItems: 'center',
  height: 24,
  margin: 2,
  backgroundColor: '#fafafa',
  border: `1px solid #e8e8e8`,
  borderRadius: 2,
  padding: '0 4px 0 10px',
  '& span': {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  '& svg': {
    fontSize: 19,
    cursor: 'pointer',
    padding: 4,
  },
}));

interface Props {
  label?: string;
  options: OptionType[];
  selectedValues: OptionType[];
  onChange: (selected: OptionType[]) => void;
}

const FixedByMultiSelect: React.FC<Props> = ({ label, options, selectedValues, onChange }) => {
  const {
    getRootProps,
    getInputLabelProps,
    getInputProps,
    getTagProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
    value,
    focused,
    setAnchorEl,
  } = useAutocomplete({
    id: 'custom-fixedby',
    multiple: true,
    options,
    getOptionLabel: (option) => option.label,
    value: selectedValues,
    isOptionEqualToValue: (a, b) => a.value === b.value,
    onChange: (_e, newVal) => onChange(newVal),
    disableCloseOnSelect: true, // ✅ This keeps the dropdown open after selection
  });

  return (
    <Root>
      <Label {...getInputLabelProps()}>{label}</Label>
      <InputWrapper ref={setAnchorEl} className={focused ? 'focused' : ''}>
        {value.map((option: OptionType, index: number) => {
          const { key, ...tagProps } = getTagProps({ index });
          return <StyledTag key={key} {...tagProps} label={option.label} />;
        })}
        <input {...getInputProps()} />
      </InputWrapper>
      {groupedOptions.length > 0 ? (
        <Listbox {...getListboxProps()}>
          {groupedOptions.map((option, index) => {
            const { key, ...optionProps } = getOptionProps({ option, index });
            return (
              <li key={key} {...optionProps}>
                <span>{option.label}</span>
                <CheckIcon fontSize="small" />
              </li>
            );
          })}
        </Listbox>
      ) : null}
    </Root>
  );
};

export default FixedByMultiSelect;
